/* ═══════════════════════════════════════════════════════════════════
   BLISS · PEACE — Yoga style catalog mapped to the 4 Animal Pillars.
   Order MATCHES the master 14-item Bliss Yoga protocol (user spec).

   Pillar semantics:
     Lion (Strength/Power)         — vigorous, heat-building
     Stallion (Endurance/Flow)     — kinetic, flowing sequences
     Falcon (Precision/Mobility)   — alignment, exact sequence, active
                                     end-range control & mobility
     Leopard (Recovery/Stealth)    — passive, restorative, gentle
   ─────────────────────────────────────────────────────────────────── */

export const PEACE_PILLARS = {
  Lion:     { color: '#C5A059', accent: '#E9C77A', label: 'STRENGTH' },
  Stallion: { color: '#B87333', accent: '#D89050', label: 'ENDURANCE' },
  Falcon:   { color: '#8FA8C7', accent: '#B8CDE5', label: 'PRECISION' },
  Leopard:  { color: '#7A5A9E', accent: '#A98BC8', label: 'RECOVERY' },
};

/** 12 yoga styles — MASTER ORDER (do not resort).
 *  Each entry:
 *   id          — matches generated image /api/uploads/peace_yoga/{id}.png
 *   name        — display name
 *   pillar      — one of Lion / Stallion / Falcon / Leopard
 *   tagline     — one-line description used on selector cards
 *   instruction — how to move / hold / pace the practice
 *   key_action  — the single most important cue to remember
 */
export const YOGA_STYLES = [
  { id: 'hatha',       name: 'Hatha',       pillar: 'Falcon',
    tagline: 'The foundation — slow, held postures',
    instruction: 'Focus on slow, deliberate movement between poses. Hold each pose for 60 seconds.',
    key_action:  'Maintain a steady, calm breath throughout. Keep your spine elongated in every posture.' },

  { id: 'restorative', name: 'Restorative', pillar: 'Leopard',
    tagline: 'Fully supported — parasympathetic reset',
    instruction: 'Use props or gravity to support your body completely. Hold each pose for 3–5 minutes.',
    key_action:  'Surrender all muscle tension. Let the body be heavy against the floor.' },

  { id: 'vinyasa',     name: 'Vinyasa',     pillar: 'Stallion',
    tagline: 'Breath-linked flow — sun & moon',
    instruction: 'Link every movement to an inhale or exhale. Flow continuously between poses without pausing.',
    key_action:  'Transition smoothly. Keep your core engaged to protect your lower back during the flow.' },

  { id: 'power',       name: 'Power',       pillar: 'Lion',
    tagline: 'Athletic vinyasa — heat & sweat',
    instruction: 'Move with intensity and speed. Focus on holding challenging poses for 45 seconds to build internal heat.',
    key_action:  'Keep your muscles active and engaged. Never "collapse" into a stretch; maintain strength throughout.' },

  { id: 'iyengar',     name: 'Iyengar',     pillar: 'Falcon',
    tagline: 'Alignment with surgical precision',
    instruction: 'Prioritize perfect alignment over depth. Adjust your limbs to strict, geometric angles (90-degree bends).',
    key_action:  'Check your posture constantly. Ensure your weight is distributed evenly across your feet or hands.' },

  { id: 'yin',         name: 'Yin',         pillar: 'Leopard',
    tagline: 'Long deep holds — connective tissue',
    instruction: 'Find your "edge" (the point of mild discomfort) and stay there. Hold for 3–5 minutes.',
    key_action:  'Do not move. Allow the muscles to remain soft so the tension can release from the connective tissues.' },

  { id: 'kundalini',   name: 'Kundalini',   pillar: 'Stallion',
    tagline: 'Coiled serpent energy at the spine',
    instruction: 'Use rhythmic, repetitive spinal movements (like "cat-cow" variations).',
    key_action:  'Incorporate rapid, powerful breathing (Breath of Fire). Maintain focus at your "third eye" (center of forehead).' },

  { id: 'jivamukti',   name: 'Jivamukti',   pillar: 'Stallion',
    tagline: 'Vigorous flow with sacred intention',
    instruction: 'Perform a vigorous Vinyasa sequence combined with focused intentions (e.g., gratitude).',
    key_action:  'Use your intention to drive the physical effort. Coordinate every movement with loud, audible breathing (Ujjayi).' },

  { id: 'ashtanga',    name: 'Ashtanga',    pillar: 'Lion',
    tagline: 'The Warrior Series — disciplined power',
    instruction: 'Follow the exact, predetermined sequence of poses every time.',
    key_action:  'Master the "Bandhas" (internal energy locks). Keep your gaze fixed on one point (Drishti) throughout the session.' },

  { id: 'hot',         name: 'Hot',         pillar: 'Lion',
    tagline: 'Heated room — vigorous sweat',
    instruction: 'Execute a vigorous series of poses in a controlled, heated environment.',
    key_action:  'Keep your breath deep and slow, even as the heart rate increases due to heat.' },

  { id: 'bikram',      name: 'Bikram',      pillar: 'Falcon',
    tagline: 'The exact 26-pose sequence',
    instruction: 'Perform the exact 26-pose sequence. Repeat each pose twice in the same order.',
    key_action:  'Follow the precise, standard instructions for every movement. Do not deviate from the sequence.' },

  { id: 'prenatal',    name: 'Prenatal',    pillar: 'Leopard',
    tagline: 'Gentle strength for two',
    instruction: 'Focus on opening the hips and relieving spinal pressure. Avoid lying flat on your back for extended periods.',
    key_action:  'Always leave ample room for your belly. Prioritize comfortable, upright, or side-lying poses.' },

  { id: 'nidra',       name: 'Yoga Nidra',  pillar: 'Leopard',
    tagline: 'Psychic sleep — full neurological reset',
    instruction: 'Lie flat on your back in Savasana. Follow a guided rotation of awareness through each body part, then breath, then imagery. Do not fall fully asleep — hover at the threshold.',
    key_action:  'Remain completely motionless. Let the body sleep while the mind stays gently aware — this is where deep subconscious recovery happens.' },

  { id: 'mobility',    name: 'Mobility Yoga', pillar: 'Falcon',
    tagline: 'Active end-range — joints & control',
    instruction: 'Move each joint actively through its full range without momentum. Include CARs (Controlled Articular Rotations) and end-range isometric holds. No passive stretching.',
    key_action:  'Own every degree of motion. If you cannot move into a position without help, you cannot control it — work only within your active range.' },
];

export const YOGA_BY_ID = Object.fromEntries(YOGA_STYLES.map(y => [y.id, y]));

export const YOGA_BY_PILLAR = ['Lion', 'Stallion', 'Falcon', 'Leopard'].reduce((acc, p) => {
  acc[p] = YOGA_STYLES.filter(y => y.pillar === p);
  return acc;
}, {});

/** Session protocol constants — 30 min total (20m active + 10m breathing) */
export const PEACE_TIMER = {
  ACTIVE_SECONDS:    20 * 60,
  BREATHING_SECONDS: 10 * 60,
  TOTAL_SECONDS:     30 * 60,
};
